import { c as _c } from "react/compiler-runtime";
import { mergeProps } from "@base-ui/react/merge-props";
import { useRender } from "@base-ui/react/use-render";
import { cn, cva, type VariantProps } from "../utils";
import { Button } from "./button";
import { Input } from "./input";
import { Textarea } from "./textarea";
function InputGroup(t0) {
  const $ = _c(10);
  let t1;
  let t2;
  let t3;
  let t4;
  if ($[0] !== t0) {
    const {
      className,
      render,
      ...props
    } = t0;
    t4 = useRender;
    t1 = "div";
    t2 = render;
    t3 = mergeProps(props, {
      role: "group",
      className: cn("group/input-group relative flex w-full items-center rounded-md border border-input shadow-xs transition-[color,box-shadow] outline-none dark:bg-input/30", "h-9 has-[>textarea]:h-auto", "has-[>[data-align=inline-start]]:[&>input]:pl-2", "has-[>[data-align=inline-end]]:[&>input]:pr-2", "has-[>[data-align=block-start]]:h-auto has-[>[data-align=block-start]]:flex-col has-[>[data-align=block-start]]:[&>input]:pb-3", "has-[>[data-align=block-end]]:h-auto has-[>[data-align=block-end]]:flex-col has-[>[data-align=block-end]]:[&>input]:pt-3", "has-[[data-slot=input-group-control]:focus-visible]:border-ring has-[[data-slot=input-group-control]:focus-visible]:ring-[3px] has-[[data-slot=input-group-control]:focus-visible]:ring-ring/50", "has-[[data-slot][aria-invalid=true]]:border-destructive has-[[data-slot][aria-invalid=true]]:ring-destructive/20 dark:has-[[data-slot][aria-invalid=true]]:ring-destructive/40", className)
    });
    $[0] = t0;
    $[1] = t1;
    $[2] = t2;
    $[3] = t3;
    $[4] = t4;
  } else {
    t1 = $[1];
    t2 = $[2];
    t3 = $[3];
    t4 = $[4];
  }
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      slot: "input-group"
    };
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== t1 || $[7] !== t2 || $[8] !== t3) {
    t6 = {
      defaultTagName: t1,
      render: t2,
      props: t3,
      state: t5
    };
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  return t4(t6);
}
const inputGroupAddonVariants = cva({
  base: "flex h-auto cursor-text items-center justify-center gap-2 py-1.5 text-sm font-medium text-muted-foreground select-none group-data-[disabled=true]/input-group:opacity-50 [&>kbd]:rounded-[calc(var(--radius)-5px)] [&>svg:not([class*='size-'])]:size-4",
  variants: {
    align: {
      "inline-start": "order-first pl-3 has-[>button]:ml-[-0.45rem] has-[>kbd]:ml-[-0.35rem]",
      "inline-end": "order-last pr-3 has-[>button]:mr-[-0.45rem] has-[>kbd]:mr-[-0.35rem]",
      "block-start": "order-first w-full justify-start px-3 pt-3 group-has-[>input]/input-group:pt-2.5 [.border-b]:pb-3",
      "block-end": "order-last w-full justify-start px-3 pb-3 group-has-[>input]/input-group:pb-2.5 [.border-t]:pt-3"
    }
  },
  defaultVariants: {
    align: "inline-start"
  }
});
function InputGroupAddon(t0) {
  const $ = _c(13);
  let align;
  let t1;
  let t2;
  let t3;
  let t4;
  if ($[0] !== t0) {
    const {
      align: t5,
      className,
      render,
      ...props
    } = t0;
    align = t5 === undefined ? "inline-start" : t5;
    t4 = useRender;
    t1 = "div";
    t2 = render;
    t3 = mergeProps(props, {
      role: "group",
      className: cn(inputGroupAddonVariants({
        align
      }), className),
      onClick: _temp
    });
    $[0] = t0;
    $[1] = align;
    $[2] = t1;
    $[3] = t2;
    $[4] = t3;
    $[5] = t4;
  } else {
    align = $[1];
    t1 = $[2];
    t2 = $[3];
    t3 = $[4];
    t4 = $[5];
  }
  let t5;
  if ($[6] !== align) {
    t5 = {
      slot: "input-group-addon",
      align
    };
    $[6] = align;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== t1 || $[9] !== t2 || $[10] !== t3 || $[11] !== t5) {
    t6 = {
      defaultTagName: t1,
      render: t2,
      props: t3,
      state: t5
    };
    $[8] = t1;
    $[9] = t2;
    $[10] = t3;
    $[11] = t5;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  return t4(t6);
}
function _temp(e) {
  if ((e.target as HTMLElement).closest("button")) {
    return;
  }
  e.currentTarget.parentElement?.querySelector("input")?.focus();
}
const inputGroupButtonVariants = cva({
  base: "flex items-center gap-2 text-sm shadow-none",
  variants: {
    size: {
      xs: "h-6 gap-1 rounded-[calc(var(--radius)-5px)] px-2 has-[>svg]:px-2 [&>svg:not([class*='size-'])]:size-3.5",
      sm: "h-8 gap-1.5 rounded-md px-2.5 has-[>svg]:px-2.5",
      "icon-xs": "size-6 rounded-[calc(var(--radius)-5px)] p-0 has-[>svg]:p-0",
      "icon-sm": "size-8 p-0 has-[>svg]:p-0"
    }
  },
  defaultVariants: {
    size: "xs"
  }
});
function InputGroupButton(t0) {
  const $ = _c(15);
  let className;
  let props;
  let t1;
  let t2;
  let t3;
  if ($[0] !== t0) {
    ({
      className,
      type: t1,
      variant: t2,
      size: t3,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
    $[3] = t1;
    $[4] = t2;
    $[5] = t3;
  } else {
    className = $[1];
    props = $[2];
    t1 = $[3];
    t2 = $[4];
    t3 = $[5];
  }
  const type = t1 === undefined ? "button" : t1;
  const variant = t2 === undefined ? "ghost" : t2;
  const size = t3 === undefined ? "xs" : t3;
  let t4;
  if ($[6] !== className || $[7] !== size) {
    t4 = cn(inputGroupButtonVariants({
      size
    }), className);
    $[6] = className;
    $[7] = size;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== props || $[10] !== size || $[11] !== t4 || $[12] !== type || $[13] !== variant) {
    t5 = <Button type={type} data-size={size} variant={variant} className={t4} {...props} />;
    $[9] = props;
    $[10] = size;
    $[11] = t4;
    $[12] = type;
    $[13] = variant;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  return t5;
}
function InputGroupText(t0) {
  const $ = _c(10);
  let t1;
  let t2;
  let t3;
  let t4;
  if ($[0] !== t0) {
    const {
      className,
      render,
      ...props
    } = t0;
    t4 = useRender;
    t1 = "span";
    t2 = render;
    t3 = mergeProps(props, {
      className: cn("flex items-center gap-2 text-sm text-muted-foreground [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4", className)
    });
    $[0] = t0;
    $[1] = t1;
    $[2] = t2;
    $[3] = t3;
    $[4] = t4;
  } else {
    t1 = $[1];
    t2 = $[2];
    t3 = $[3];
    t4 = $[4];
  }
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      slot: "input-group-text"
    };
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== t1 || $[7] !== t2 || $[8] !== t3) {
    t6 = {
      defaultTagName: t1,
      render: t2,
      props: t3,
      state: t5
    };
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  return t4(t6);
}
function InputGroupInput(t0) {
  const $ = _c(10);
  let className;
  let props;
  let ref;
  if ($[0] !== t0) {
    ({
      className,
      ref,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
    $[3] = ref;
  } else {
    className = $[1];
    props = $[2];
    ref = $[3];
  }
  let t1;
  if ($[4] !== className) {
    t1 = cn("flex-1 rounded-none border-0 !bg-transparent shadow-none focus-visible:ring-0", className);
    $[4] = className;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== props || $[7] !== ref || $[8] !== t1) {
    t2 = <Input ref={ref} data-slot="input-group-control" className={t1} {...props} />;
    $[6] = props;
    $[7] = ref;
    $[8] = t1;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  return t2;
}
function InputGroupTextarea(t0) {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("flex-1 resize-none rounded-none border-0 !bg-transparent py-3 shadow-none focus-visible:ring-0", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <Textarea data-slot="input-group-control" className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
export { InputGroup, InputGroupAddon, InputGroupButton, InputGroupInput, InputGroupText, InputGroupTextarea };